/*
 * Razorpay Checkout, opened on top of our own page.
 *
 * The alternative - and what this replaces - is Razorpay's hosted page in a second tab.
 * That works, but it leaves the customer standing on Razorpay's "payment successful"
 * screen with no way back, having to find our tab again themselves; enough of them decide
 * the purchase did not go through. The popup keeps them here, so when it closes they are
 * already looking at the row that is about to change.
 *
 * What comes back from `handler` is not proof of anything. It is the browser's word, and
 * the browser is the one place we never take a payment's word from - the plan still moves
 * only when Razorpay's signed webhook reaches the server. All the popup buys is knowing
 * when to start watching, and that is all the caller should use it for.
 */

const SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

let loading = null;

function loadScript() {
    if (window.Razorpay) return Promise.resolve(true);
    if (!loading) {
        loading = new Promise((resolve) => {
            const el = document.createElement("script");
            el.src = SCRIPT;
            el.onload = () => resolve(true);
            // Let the next attempt try again rather than caching the failure for the
            // life of the page - a blip on one click should not kill every later one.
            el.onerror = () => { loading = null; resolve(false); };
            document.body.appendChild(el);
        });
    }
    return loading;
}

/**
 * Open the popup for a subscription that has already been created on the server.
 *
 * Resolves with what the customer did: "paid" (they finished - start watching),
 * "dismissed" (closed it - the row stays awaiting payment and the link still works),
 * "failed", or "fallback" when the script could not load and Razorpay's own page was
 * opened in a tab instead.
 */
export default async function openCheckout({
    keyId, subscriptionId, payUrl, email, description,
}) {
    const ready = keyId && subscriptionId && (await loadScript());
    if (!ready) {
        // No popup, but never a dead end: the hosted page does the same job.
        if (payUrl) window.open(payUrl, "_blank", "noopener");
        return "fallback";
    }

    return new Promise((resolve) => {
        const rz = new window.Razorpay({
            key: keyId,
            subscription_id: subscriptionId,
            name: "MatrixIQ",
            description,
            prefill: email ? { email } : undefined,
            theme: { color: "#2E7D20" },
            handler: () => resolve("paid"),
            modal: { ondismiss: () => resolve("dismissed") },
        });
        rz.on("payment.failed", () => resolve("failed"));
        rz.open();
    });
}
